import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Where to land after signing in: back to the deep link that started the
 * session flow, or the dashboard for a plain visit. Only internal paths. */
export function redirectAfterLogin(search: string | Record<string, string> | URLSearchParams): string {
  const raw = search instanceof URLSearchParams
    ? search.get("redirect")
    : typeof search === "string"
      ? new URLSearchParams(search).get("redirect")
      : (search as Record<string, string>).redirect;
  if (!raw) return "/dashboard";
  if (!raw.startsWith("/") || raw.startsWith("//")) return "/dashboard";
  return raw;
}

/** Parse a date-only string ("2026-08-26") as a LOCAL date. `new Date(...)`
 * reads it as UTC midnight, so US timezones displayed it a day early — CDL
 * expiry and medical-card dates were off by one. */
export function parseDateOnly(value?: string | null): Date | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** Parse timestamps ("2026-08-26T14:00:00") keeping the time of day, while
 * still treating bare dates as local. Using parseDateOnly for an interview
 * start silently turned every slot into 00:00. */
export function parseDateTime(value?: string | null): Date | null {
  if (!value) return null;
  if (/T\d{2}:\d{2}/.test(value) || /\s\d{2}:\d{2}/.test(value)) {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return parseDateOnly(value);
}

/** Format numbers with locale separators: 12345 → "12 345" / "12,345". */
export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return value.toLocaleString();
}

/**
 * Keep user-chosen stage colours as a tint. Text follows the active theme:
 * choosing white from the original colour's luminance is unreadable once
 * that colour becomes a translucent fill on a light surface.
 */
export function badgeColorStyle(color?: string | null): React.CSSProperties {
  if (!color) return {};
  const hex = color.replace("#", "");
  const full = hex.length === 3 ? hex.split("").map((c) => c + c).join("") : hex;
  if (full.length !== 6 || /[^0-9a-f]/i.test(full)) return {};
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  return {
    backgroundColor: `rgba(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)}, .14)`,
    color: "var(--text-strong)",
    fontWeight: 700,
  };
}

/** Robust copy to clipboard supporting modern clipboard API and fallback for non-HTTPS / IP environments */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (!text) return false;
  if (typeof navigator !== "undefined" && navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to execCommand
    }
  }

  try {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.top = "-9999px";
    textArea.style.left = "-9999px";
    textArea.style.opacity = "0";
    textArea.setAttribute("readonly", "");
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const successful = document.execCommand("copy");
    document.body.removeChild(textArea);
    return successful;
  } catch {
    return false;
  }
}
